import React, { useEffect } from 'react';
import { MapContainer, TileLayer, GeoJSON, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import type { Geometry } from '../../types/gis';
import { getCartoTileUrl } from '../../utils/mapTiles';

export interface ShareCardTemplateProps {
  title: string;
  icon?: React.ReactNode;
  themeColor: 'blue' | 'red' | 'orange' | 'indigo' | 'slate' | 'rose' | 'amber' | 'purple' | 'emerald';
  data: { label: string; value: string; isPill?: boolean }[];
  innerRef?: React.RefObject<HTMLDivElement | null>;
  geometry?: Geometry | null;
  pointLocation?: [number, number] | null; // Optional [lng, lat] to draw a point over boundary
  shareLabel?: string;
}

const gradientMap = {
  blue: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
  red: 'linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)',
  orange: 'linear-gradient(135deg, #7c2d12 0%, #f97316 100%)',
  indigo: 'linear-gradient(135deg, #312e81 0%, #6366f1 100%)',
  slate: 'linear-gradient(135deg, #1e293b 0%, #64748b 100%)',
  rose: 'linear-gradient(135deg, #881337 0%, #e11d48 100%)',
  amber: 'linear-gradient(135deg, #92400e 0%, #f59e0b 100%)',
  purple: 'linear-gradient(135deg, #701a75 0%, #d946ef 100%)',
  emerald: 'linear-gradient(135deg, #064e3b 0%, #10b981 100%)'
};

const accentMap = {
  blue: '#3b82f6',
  red: '#ef4444',
  orange: '#f97316',
  indigo: '#6366f1',
  slate: '#64748b',
  rose: '#e11d48',
  amber: '#f59e0b',
  purple: '#d946ef',
  emerald: '#10b981'
};

// Simple component to auto-fit map viewport to geometry on render
const FitBounds: React.FC<{ geometry: Geometry }> = ({ geometry }) => {
  const map = useMap();
  useEffect(() => {
    if (!geometry) return;
    
    // Critical latency delay gives browser adequate CPU cycle time to paint CSS height 
    // onto hidden absolute element so map.invalidateSize receives real metrics.
    const timer = setTimeout(() => {
      try {
        // Recalculate hidden node viewport now that layout flow is calculated
        map.invalidateSize({ animate: false });
        
        if (geometry.type === 'Point') {
          const [lng, lat] = (geometry as any).coordinates;
          map.setView([lat, lng], 15, { animate: false });
        } else {
          const layer = L.geoJSON(geometry);
          const bounds = layer.getBounds();
          if (bounds.isValid()) {
            // Apply perfect bounds fit with breathable padding. 
            // Removed hard zoom override to guarantee the FULL boundary is visible every single time.
            map.fitBounds(bounds, { padding: [40, 40], animate: false, maxZoom: 16 });
          }
        }
      } catch (e) {
        console.error('FitBounds async error', e);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [geometry, map]);
  return null;
};

const GeometryPreview: React.FC<{ 
  geometry: Geometry; 
  accentColor: string; 
  themeColor: string;
  pointLocation?: [number, number] | null;
}> = ({ geometry, accentColor, pointLocation }) => {
  // Force elegant light mode for clean visibility on share card
  const mapUrl = getCartoTileUrl('light_all');

  // Derive a distinct, robust key ensuring complete instance re-creation when geometry swaps
  const geoString = JSON.stringify(geometry);
  const mapKey = `share-map-${geoString.length}-${geoString.slice(geoString.length - 30)}`;

  return (
    <div style={{
      width: '100%',
      height: '240px',
      flexShrink: 0, // CRITICAL: Absolutely prevent flexbox from squashing the map view
      marginTop: '24px',
      borderRadius: '20px',
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden',
      boxShadow: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)'
    }}>
      {/* Label overlay for visual depth */}
      <div style={{
        position: 'absolute',
        top: '12px',
        left: '12px',
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(4px)',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 700,
        color: '#1e293b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        Context Map
      </div>

      <div style={{ width: '100%', height: '100%', position: 'relative' }}>
        {/* Utilizing mapKey to force a COMPLETELY fresh render context for guaranteed container resolution */}
        <MapContainer
          key={mapKey}
          center={[11.1271, 78.6569]}
          zoom={12}
          zoomControl={false}
          attributionControl={false}
          scrollWheelZoom={false}
          dragging={false}
          doubleClickZoom={false}
          style={{ width: '100%', height: '100%' }}
        >
          <TileLayer
            url={mapUrl}
            crossOrigin="anonymous"
          />
          {geometry.type === 'Point' ? (
            <>
              {/* Beautiful glow surrounding the target dot */}
              <CircleMarker
                center={[(geometry as any).coordinates[1], (geometry as any).coordinates[0]]}
                radius={16}
                pathOptions={{
                  color: accentColor,
                  fillColor: accentColor,
                  fillOpacity: 0.15,
                  weight: 0
                }}
              />
              <CircleMarker 
                center={[(geometry as any).coordinates[1], (geometry as any).coordinates[0]]}
                radius={8}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: accentColor,
                  fillOpacity: 1,
                  weight: 3,
                  shadowBlur: 4,
                  shadowColor: 'rgba(0,0,0,0.3)'
                } as any}
              />
            </>
          ) : (
            <GeoJSON 
              key={`geojson-${geoString.length}`}
              data={geometry}
              style={{
                color: accentColor,
                weight: 3,
                fillColor: accentColor,
                fillOpacity: 0.1,
                dashArray: '5, 5'
              }}
            />
          )}

          {/* Reusable Auxiliary target marker overlaid ON TOP of boundary if distinct pointLocation exists */}
          {pointLocation && (
            <>
              <CircleMarker
                center={[pointLocation[1], pointLocation[0]]}
                radius={16}
                pathOptions={{
                  color: accentColor,
                  fillColor: accentColor,
                  fillOpacity: 0.15,
                  weight: 0
                }}
              />
              <CircleMarker 
                center={[pointLocation[1], pointLocation[0]]}
                radius={8}
                pathOptions={{
                  color: '#ffffff',
                  fillColor: accentColor,
                  fillOpacity: 1,
                  weight: 3,
                  shadowBlur: 4,
                  shadowColor: 'rgba(0,0,0,0.3)'
                } as any}
              />
            </>
          )}
          
          {/* Internal map logic component handles precise fit calculation */}
          <FitBounds geometry={geometry} />
        </MapContainer>
      </div>
    </div>
  );
};

// Helper to ensure strings don't contain malformed UTF-16 surrogates which crash encodeURIComponent
const safeText = (text: string): string => {
  if (!text) return '';
  const str = String(text);
  if ((str as any).toWellFormed) return (str as any).toWellFormed();
  return str.replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g, '\uFFFD');
};

export const ShareCardTemplate: React.FC<ShareCardTemplateProps> = ({
  title,
  icon,
  themeColor,
  data,
  innerRef,
  geometry,
  pointLocation,
  shareLabel
}) => {
  const safeTitle = safeText(title);
  const bgGradient = gradientMap[themeColor] || gradientMap.blue;
  const accentColor = accentMap[themeColor] || accentMap.blue;

  // Show up to 6 items to encompass all relevant info
  // Dynamically filter out development-only metrics like 'Match Confidence' from the share visuals
  const previewData = data
    .filter(item => !/match confidence|accuracy/i.test(item.label || ''))
    .slice(0, 6);
  const isCompact = previewData.length > 4;

  return (
    <div
      ref={innerRef}
      className="share-card-render-host"
      style={{
        width: '800px',
        height: geometry ? '1080px' : '900px', // Increased height to prevent cropping on long data lists
        background: bgGradient,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isCompact ? '24px' : '40px',
        boxSizing: 'border-box',
        fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
        color: '#ffffff',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Dynamic Abstract Background Detail */}
      <div style={{
        position: 'absolute',
        top: '-100px',
        right: '-100px',
        width: '400px',
        height: '400px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        filter: 'blur(60px)'
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-50px',
        left: '-50px',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'rgba(0, 0, 0, 0.2)',
        filter: 'blur(50px)'
      }} />

      {/* Mascot Background Detail - Top Left Peeking */}
      <img 
        src="/branding/mascot.webp" 
        alt="N-Bot" 
        crossOrigin="anonymous"
        style={{
          position: 'absolute',
          top: '60px',
          left: '10px',
          width: '240px',
          height: 'auto',
          opacity: 0.9, // Highly visible as requested
          transform: 'rotate(-15deg)',
          pointerEvents: 'none',
          zIndex: 10 // Stay in front
        }}
      />

      {/* Central Glass Card */}
      <div style={{
        width: '100%',
        flexShrink: 0, // Ensure internal card geometry is preserved
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '32px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        padding: isCompact ? '32px 40px' : '40px 48px',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 2, // Elevated above mascot
        backdropFilter: 'blur(10px)',
        position: 'relative' // Needed for zIndex to work correctly
      }}>
        
        {/* Category Icon Section */}
        <div style={{
          width: isCompact ? '70px' : '90px',
          height: isCompact ? '70px' : '90px',
          borderRadius: '20px',
          background: `${accentColor}15`,
          color: accentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: isCompact ? '12px' : '20px',
          transform: isCompact ? 'scale(1.2)' : 'scale(1.5)'
        }}>
          {/* Render explicit size adjustment for lucide icon cloning */}
          {icon && React.cloneElement(icon as React.ReactElement, { size: isCompact ? 32 : 40 } as any)}
        </div>

        {/* Category Label Section */}
        {shareLabel && (
          <div style={{
            background: `${accentColor}10`,
            color: accentColor,
            padding: '6px 14px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            marginBottom: '12px',
            border: `1px solid ${accentColor}20`
          }}>
            {shareLabel}
          </div>
        )}

        {/* Main Info */}
        <h2 style={{
          margin: isCompact ? '0 0 20px 0' : '0 0 28px 0',
          fontSize: isCompact ? '32px' : '40px',
          fontWeight: 800,
          textAlign: 'center',
          color: '#111827',
          lineHeight: 1.2,
          width: '100%',
          wordBreak: 'break-word'
        }}>
          {safeTitle}
        </h2>

        {/* Details Table */}
        <div style={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: isCompact ? '10px' : '14px',
          marginBottom: '4px'
        }}>
          {previewData.map((item, i) => (
            <div 
              key={i}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f8fafc',
                borderRadius: isCompact ? '12px' : '16px',
                padding: isCompact ? '14px 20px' : '18px 24px',
                border: '1px solid #e2e8f0',
                gap: '16px'
              }}
            >
              <span style={{
                fontSize: isCompact ? '13px' : '15px',
                fontWeight: 600,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                flexShrink: 0
              }}>
                {/* Remove emojis using a robust Unicode property escape (requires ES2018+) */}
                {item.label.replace(/\p{Emoji}/gu, '').replace(/[📍🏢💡🗺️ℹ️📦]/g, '').trim()}
              </span>
              <span style={{
                fontSize: isCompact ? '18px' : '22px',
                fontWeight: 700,
                color: item.isPill ? accentColor : '#1e293b',
                textAlign: 'right',
                wordBreak: 'break-word'
              }}>
                {safeText(item.value)}
              </span>
            </div>
          ))}
        </div>

        {/* Dynamic Boundary Preview with actual context map background */}
        {geometry && (
          <GeometryPreview 
            geometry={geometry} 
            accentColor={accentColor} 
            themeColor={themeColor} 
            pointLocation={pointLocation}
          />
        )}
      </div>

      {/* Branding Footer */}
      <div style={{
        marginTop: '40px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        zIndex: 2
      }}>
        <img 
          src="/branding/icon.png" 
          alt="Logo" 
          crossOrigin="anonymous"
          style={{ width: '40px', height: '40px' }}
        />
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span style={{
            fontSize: '24px',
            fontWeight: 800,
            letterSpacing: '-0.5px'
          }}>
            NammaMap
          </span>
          <span style={{
            fontSize: '14px',
            fontWeight: 600,
            opacity: 0.8
          }}>
            nammamap.in
          </span>
        </div>
      </div>
    </div>
  );
};
